import { createFileRoute, Link } from "@tanstack/react-router";
import { useLang } from "@/hooks/use-language";
import { LegalShell } from "@/components/legal/LegalShell";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/privacy" },
      { title: "Privacy Policy — Jozori | سياسة الخصوصية — جذوري" },
      {
        name: "description",
        content:
          "How Jozori protects children's privacy and family data. كيف تحمي جذوري خصوصية الأطفال وبيانات العائلة.",
      },
    ],
    links: [{ rel: "canonical", href: "https://jozori.com/privacy" }],
  }),
});

function PrivacyPage() {
  const { lang } = useLang();
  const isAr = lang === "ar";

  return (
    <LegalShell
      titleEn="Privacy Policy"
      titleAr="سياسة الخصوصية"
      updatedEn="May 19, 2026"
      updatedAr="19 مايو 2026"
    >
      {isAr ? <Ar /> : <En />}
    </LegalShell>
  );
}

function En() {
  return (
    <>
      <p>
        Bilal Almadani ("Jozori", "we", "us") operates the Jozori bilingual
        learning platform for children and families. We are the data controller for
        the personal data described below. We take the privacy of children, parents,
        and educators extremely seriously, and we design our product to comply with
        the spirit of COPPA (US) and GDPR / GDPR-K (EU/UK).
      </p>

      <h2>1. Who creates accounts</h2>
      <p>
        Only a parent or legal guardian aged 18 or older creates an account. Child
        profiles live inside a parent account and are never separately registered.
        Children cannot directly sign up, log in, message anyone, or share
        information with strangers.
      </p>

      <h2>2. What we collect</h2>
      <ul>
        <li><strong>Parent account:</strong> email, display name, language preference, hashed PIN.</li>
        <li><strong>Child profile:</strong> first name or nickname, age tier, chosen avatar, learning progress.</li>
        <li><strong>Usage telemetry:</strong> activities completed, time spent, badges earned — used solely to power the parent dashboard and personalise content.</li>
        <li><strong>Device basics:</strong> IP address, device type, and crash diagnostics for security and service reliability.</li>
      </ul>
      <p>We do <strong>not</strong> collect: precise location, behavioural advertising identifiers, contacts, photos from the device, microphone recordings, or biometric data.</p>

      <h2>3. Legal basis (UK/EEA users)</h2>
      <ul>
        <li><strong>Performance of a contract</strong> — operating the service you subscribed to.</li>
        <li><strong>Legitimate interests</strong> — security, fraud prevention, and product improvement.</li>
        <li><strong>Consent</strong> — optional marketing emails (withdraw any time from the email footer or Settings).</li>
        <li><strong>Legal obligation</strong> — tax, accounting, and regulatory requirements.</li>
      </ul>

      <h2>4. How we use data</h2>
      <p>
        Strictly to operate the service: deliver age-appropriate content, show
        parents their child's progress, secure the account, prevent abuse, and
        improve the product. We do not sell data, and we do not show third-party
        advertising to children.
      </p>

      <h2>5. Sharing and subprocessors</h2>
      <p>Data is processed by trusted providers bound by data-processing agreements:</p>
      <ul>
        <li><strong>Hosting & database</strong> — for storing your family's account.</li>
        <li><strong>Email delivery</strong> — for transactional emails (account, billing, weekly summaries).</li>
        <li><strong>Paddle.com</strong> — our Merchant of Record. Paddle handles payments, billing, sales tax, invoicing, and refunds, and may collect billing-related data (name, billing address, payment method, IP, location of purchase). See Paddle's privacy notice for details.</li>
        <li><strong>Professional advisers</strong> — legal and accounting, where required.</li>
        <li><strong>Authorities</strong> — where required by law.</li>
      </ul>
      <p>We never share child data with marketers, data brokers, or advertising networks.</p>

      <h2>6. International transfers</h2>
      <p>
        Some subprocessors may process data outside the UK/EEA. In those cases we
        rely on appropriate safeguards (such as the EU Standard Contractual Clauses
        and the UK International Data Transfer Addendum, or equivalent adequacy
        decisions).
      </p>

      <h2>7. Retention</h2>
      <p>
        Account and child-profile data is kept while your account is active and for
        up to 12 months after cancellation, then deleted or anonymised. Billing
        records retained by Paddle may be kept longer to meet tax and accounting
        obligations.
      </p>

      <h2>8. Your rights</h2>
      <p>
        Parents can view, export, correct, or permanently delete their family's
        data from <em>Settings → Privacy</em>, or by emailing{" "}
        <a href="mailto:privacy@jozori.com">privacy@jozori.com</a>. UK/EEA
        users also have the right to restrict or object to processing, withdraw
        consent, request portability, and lodge a complaint with their local data
        protection authority. We respond to verified requests within 30 days.
      </p>

      <h2>9. Security</h2>
      <p>
        We use industry-standard technical and organisational measures including
        encryption in transit and at rest, hashed credentials, role-based access
        controls, audit logging, and regular security review.
      </p>

      <h2>10. Cookies</h2>
      <p>
        We use only essential cookies required to keep you signed in and to protect
        the service from abuse. We do not use third-party advertising or
        cross-site tracking cookies on children's surfaces.
      </p>

      <h2>11. Contact</h2>
      <p>
        Bilal Almadani — email{" "}
        <a href="mailto:privacy@jozori.com">privacy@jozori.com</a> for any privacy
        question, or <a href="mailto:safety@jozori.com">safety@jozori.com</a> for
        anything related to child safety. See also our{" "}
        <Link to="/safety">Child Safety page</Link>.
      </p>
    </>
  );
}

function Ar() {
  return (
    <>
      <p>
        يُدير بلال المداني ("جذوري"، "نحن") منصة جذوري العائلية
        ثنائية اللغة للأطفال والعائلات، ونعدّ المتحكّم بالبيانات الشخصية
        الموضّحة أدناه. نحن نأخذ خصوصية الأطفال والآباء والمربّين على محمل
        الجدّ، ونصمّم منتجنا ليتوافق مع روح قانون حماية خصوصية الأطفال على
        الإنترنت COPPA (الولايات المتحدة) واللائحة العامة لحماية البيانات GDPR
        وأحكامها الخاصة بالأطفال GDPR-K (الاتحاد الأوروبي/المملكة المتحدة).
      </p>

      <h2>١. من ينشئ الحسابات</h2>
      <p>
        لا ينشئ الحساب إلا أحد الوالدَين أو الوليّ القانوني البالغ من العمر 18
        عامًا فأكثر. تعيش ملفات الأطفال داخل حساب الوالد ولا تُسجَّل بشكل
        منفصل. لا يستطيع الأطفال التسجيل أو تسجيل الدخول أو مراسلة أحد أو
        مشاركة معلومات مع غرباء بأنفسهم.
      </p>

      <h2>٢. ما الذي نجمعه</h2>
      <ul>
        <li><strong>حساب الوالد:</strong> البريد الإلكتروني، اسم العرض، تفضيل اللغة، رمز PIN مُشفَّر.</li>
        <li><strong>ملف الطفل:</strong> الاسم الأول أو الكنية، الفئة العمرية، الشخصية المختارة، النمو والتقدّم.</li>
        <li><strong>بيانات الاستخدام:</strong> الأنشطة المُنجَزة، الوقت المُستهلَك، الأوسمة المكتسبة — تُستخدم حصرًا لتشغيل لوحة الوالد وتخصيص المحتوى.</li>
        <li><strong>بيانات الجهاز الأساسية:</strong> عنوان IP، نوع الجهاز، وتشخيصات الأعطال لأغراض الأمان وموثوقية الخدمة.</li>
      </ul>
      <p>
        نحن <strong>لا</strong> نجمع: الموقع الجغرافي الدقيق، أو معرّفات الإعلانات
        السلوكية، أو جهات الاتصال، أو الصور من الجهاز، أو تسجيلات الميكروفون، أو
        البيانات البيومترية.
      </p>

      <h2>٣. الأساس القانوني (مستخدمو المملكة المتحدة والمنطقة الاقتصادية الأوروبية)</h2>
      <ul>
        <li><strong>تنفيذ العقد</strong> — تشغيل الخدمة التي اشتركتَ بها.</li>
        <li><strong>المصالح المشروعة</strong> — الأمان، ومنع الاحتيال، وتحسين المنتج.</li>
        <li><strong>الموافقة</strong> — رسائل التسويق الاختيارية (يمكن سحبها في أي وقت من تذييل البريد أو من الإعدادات).</li>
        <li><strong>الالتزام القانوني</strong> — المتطلبات الضريبية والمحاسبية والتنظيمية.</li>
      </ul>

      <h2>٤. كيف نستخدم البيانات</h2>
      <p>
        حصرًا لتشغيل الخدمة: تقديم محتوى مناسب للعمر، إظهار تقدّم الطفل للوالدَين،
        تأمين الحساب، منع إساءة الاستخدام، وتحسين المنتج. نحن لا نبيع البيانات،
        ولا نعرض إعلانات طرف ثالث للأطفال.
      </p>

      <h2>٥. المشاركة والمعالجون من الباطن</h2>
      <p>تتم معالجة البيانات عبر مزوّدين موثوقين مرتبطين باتفاقيات معالجة بيانات:</p>
      <ul>
        <li><strong>الاستضافة وقاعدة البيانات</strong> — لتخزين حساب عائلتك.</li>
        <li><strong>إرسال البريد الإلكتروني</strong> — للرسائل المعاملاتية (الحساب، الفوترة، الملخّص الأسبوعي).</li>
        <li><strong>Paddle.com</strong> — تاجر التسجيل الخاص بنا. تتولّى Paddle المدفوعات والفوترة والضرائب والفواتير وعمليات الاسترداد، وقد تجمع بيانات تتعلّق بالفوترة (الاسم، عنوان الفوترة، طريقة الدفع، عنوان IP، موقع الشراء). راجع إشعار خصوصية Paddle للتفاصيل.</li>
        <li><strong>المستشارون المهنيون</strong> — قانونيًا ومحاسبيًا، عند الحاجة.</li>
        <li><strong>الجهات الرسمية</strong> — حين يفرض القانون ذلك.</li>
      </ul>
      <p>لا نشارك أبدًا بيانات الأطفال مع المسوّقين أو وسطاء البيانات أو شبكات الإعلانات.</p>

      <h2>٦. النقل الدولي للبيانات</h2>
      <p>
        قد تعالج بعض الجهات بيانات خارج المملكة المتحدة والمنطقة الاقتصادية
        الأوروبية. في هذه الحالات نعتمد على ضمانات مناسبة (مثل البنود التعاقدية
        النموذجية للاتحاد الأوروبي، وملحق نقل البيانات الدولي للمملكة المتحدة،
        أو قرارات الكفاية المماثلة).
      </p>

      <h2>٧. مدة الاحتفاظ</h2>
      <p>
        نحتفظ ببيانات الحساب وملفات الأطفال طوال فترة نشاط حسابك ولمدة تصل إلى
        12 شهرًا بعد الإلغاء، ثم تُحذف أو تُجعَل مجهولة الهوية. قد تحتفظ Paddle
        بسجلات الفوترة لمدة أطول للوفاء بالالتزامات الضريبية والمحاسبية.
      </p>

      <h2>٨. حقوقك</h2>
      <p>
        يمكن للوالدَين عرض بيانات العائلة أو تصديرها أو تصحيحها أو حذفها نهائيًا
        من <em>الإعدادات ← الخصوصية</em>، أو بالمراسلة على{" "}
        <a href="mailto:privacy@jozori.com">privacy@jozori.com</a>. يتمتّع
        مستخدمو المملكة المتحدة والمنطقة الاقتصادية الأوروبية كذلك بحق تقييد
        المعالجة أو الاعتراض عليها، وسحب الموافقة، وطلب نقل البيانات، وتقديم
        شكوى لدى سلطة حماية البيانات المحلية. نردّ على الطلبات الموثَّقة خلال
        30 يومًا.
      </p>

      <h2>٩. الأمان</h2>
      <p>
        نطبّق تدابير فنية وتنظيمية تتوافق مع معايير الصناعة، تشمل التشفير أثناء
        النقل وفي حالة السكون، وتجزئة بيانات الاعتماد، وضوابط الوصول حسب الدور،
        وتدوين سجلات التدقيق، ومراجعات أمنية دورية.
      </p>

      <h2>١٠. ملفات الارتباط (Cookies)</h2>
      <p>
        نستخدم فقط ملفات الارتباط الضرورية لإبقائك مسجَّلًا ولحماية الخدمة من
        إساءة الاستخدام. لا نستخدم ملفات ارتباط إعلانات طرف ثالث أو تتبّع عبر
        المواقع على واجهات الأطفال.
      </p>

      <h2>١١. التواصل</h2>
      <p>
        Bilal Almadani — راسلنا على{" "}
        <a href="mailto:privacy@jozori.com">privacy@jozori.com</a> لأي سؤال
        يتعلّق بالخصوصية، أو على{" "}
        <a href="mailto:safety@jozori.com">safety@jozori.com</a> لأي شأن يتعلّق
        بسلامة الأطفال. راجع أيضًا{" "}
        <Link to="/safety">صفحة سلامة الأطفال</Link>.
      </p>
    </>
  );
}
